/**
 * directory_map_controller.js — the /find directory: a clustered map of trade
 * businesses and the list that follows it.
 *
 * One GeoJSON feed (/find/listings.geojson) is fetched once per visit. The trade
 * chips and the search box filter it in the browser; the map shows the result
 * as clusters that open into points, and the list shows whatever is in view,
 * nearest the centre first. Picking a card flies the map to it; picking a point
 * highlights its card.
 *
 * The map is MapLibre straight onto Radar's vector style (no Leaflet layer,
 * which the single-pin detail maps need and this page does not). MapLibre loads
 * through _load_script.js only when there is a style to draw and WebGL to draw
 * it with; otherwise the page is the list alone, with the panel full width.
 *
 * Phone layout: the panel is a bottom sheet with three heights (peek, half,
 * full) moved by tapping or dragging its handle. Wide screens ignore the sheet
 * state through CSS.
 *
 * The search box filters names and suburbs as they type. Enter on text that
 * matches nothing ("handyman near mayfield") posts it to /find/search, and the
 * answer presses a trade chip and moves the map, through the same code a chip
 * tap runs. A submitted search that leaves no business in view is reported to
 * /find/missed, which logs it as a gap in supply.
 *
 * Values (from templates/directory/index.html):
 *   feedUrl, searchUrl, missedUrl, csrf, styleUrl, maplibreJs, center [lng, lat], zoom, trade
 */

import { Controller } from "@hotwired/stimulus";
import {
    buildCard, buildPopup, countLabel, featureMatches, LIST_LIMIT, visibleFeatures,
} from "./_directory_cards.js";
import { loadScript } from "./_load_script.js";

const SOURCE = 'find-businesses';
const SELECTED = 'find-selected';
const CLUSTER_FONT = ['Graphik Semibold', 'Noto Sans Semibold'];
const SHEET_STATES = ['peek', 'half', 'full'];
const SEARCH_DELAY_MS = 150;
const PICK_ZOOM = 14;
const NEAR_ZOOM = 12;
// A searched suburb fills the view with room for its neighbours.
const PLACE_ZOOM = 13;
// Matches the side-panel breakpoint in directory.css.
const WIDE_QUERY = '(min-width: 900px)';

export default class extends Controller {
    static targets = ['map', 'list', 'count', 'search', 'sheet', 'near', 'chip'];
    static values = {
        feedUrl: String,
        searchUrl: String,
        missedUrl: String,
        csrf: String,
        styleUrl: String,
        maplibreJs: String,
        center: Array,
        zoom: Number,
        trade: String,
    };

    connect() {
        this._features = [];
        this._query = '';
        this._sheet = 'half';
        this.sheetTarget.dataset.sheet = this._sheet;
        this._loadFeed();
        if (this.styleUrlValue && hasWebGL()) {
            loadScript(this.maplibreJsValue, 'maplibregl')
                .then(() => this._buildMap())
                .catch(() => this._listOnly());
        } else {
            this._listOnly();
        }
    }

    disconnect() {
        clearTimeout(this._searchTimer);
        this._endDrag();
        if (this._map) this._map.remove();
        this._map = null;
        this._mapReady = false;
    }

    // ------------------------------------------------------------------
    // Data
    // ------------------------------------------------------------------

    _loadFeed() {
        fetch(this.feedUrlValue, { headers: { Accept: 'application/geo+json' } })
            .then((response) => {
                if (!response.ok) throw new Error(`feed ${response.status}`);
                return response.json();
            })
            .then((collection) => {
                this._features = collection.features;
                this._apply();
            })
            .catch(() => {
                this.countTarget.textContent = 'The map could not load. Refresh to try again.';
            });
    }

    _filtered() {
        return this._features.filter((f) => featureMatches(f, this.tradeValue, this._query));
    }

    _apply() {
        const source = this._map && this._map.getSource(SOURCE);
        if (source) source.setData({ type: 'FeatureCollection', features: this._filtered() });
        this._renderList();
    }

    // ------------------------------------------------------------------
    // Map
    // ------------------------------------------------------------------

    _buildMap() {
        const maplibregl = window.maplibregl;
        this._map = new maplibregl.Map({
            container: this.mapTarget,
            style: this.styleUrlValue,
            center: this.centerValue,
            zoom: this.zoomValue,
            attributionControl: false,
        });
        this._map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
        // On a phone the bottom corners sit under the sheet, so the credit goes
        // to the top where it stays readable.
        const wide = window.matchMedia(WIDE_QUERY).matches;
        this._map.addControl(new maplibregl.AttributionControl({ compact: true }),
            wide ? 'bottom-right' : 'top-left');
        this._map.on('load', () => this._addLayers());
        this._map.on('moveend', () => this._renderList());
    }

    _addLayers() {
        const map = this._map;
        const colours = this._colours();
        map.addSource(SOURCE, {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: this._filtered() },
            cluster: true,
            clusterRadius: 46,
            clusterMaxZoom: 14,
        });
        map.addSource(SELECTED, { type: 'geojson', data: emptyCollection() });
        map.addLayer(clusterLayer(colours));
        map.addLayer(clusterCountLayer());
        map.addLayer(pointLayer(colours));
        map.addLayer(selectedLayer(colours));
        this._bindLayerEvents();
        // Ready once our layers exist. map.loaded() stays false while tiles
        // stream in, which would leave the list ignoring the view.
        this._mapReady = true;
        this._renderList();
    }

    _bindLayerEvents() {
        const map = this._map;
        map.on('click', 'find-clusters', (event) => this._openCluster(event.features[0]));
        map.on('click', 'find-points', (event) => this._select(event.features[0], false));
        for (const layer of ['find-clusters', 'find-points']) {
            map.on('mouseenter', layer, () => { map.getCanvas().style.cursor = 'pointer'; });
            map.on('mouseleave', layer, () => { map.getCanvas().style.cursor = ''; });
        }
    }

    async _openCluster(cluster) {
        const zoom = await this._map.getSource(SOURCE)
            .getClusterExpansionZoom(cluster.properties.cluster_id);
        this._map.easeTo({ center: cluster.geometry.coordinates, zoom: zoom + 0.5 });
    }

    _colours() {
        const style = getComputedStyle(this.element);
        return {
            chippie: style.getPropertyValue('--find-chippie').trim() || '#E85D1F',
            listing: style.getPropertyValue('--find-listing').trim() || '#1F4D35',
            cluster: style.getPropertyValue('--find-cluster').trim() || '#1F4D35',
        };
    }

    _listOnly() {
        this.element.classList.add('find--list-only');
        this._renderList();
    }

    // ------------------------------------------------------------------
    // List and selection
    // ------------------------------------------------------------------

    _renderList() {
        if (!this._features.length) return;
        const inView = this._inView();
        const shown = inView.slice(0, LIST_LIMIT);
        this.listTarget.replaceChildren(...shown.map(buildCard));
        this.countTarget.textContent = countLabel(shown.length, inView.length, this._hasMap());
        this._markSelectedCard();
    }

    _hasMap() {
        return !!(this._map && this._mapReady);
    }

    /** The filtered businesses inside the map's view, nearest the centre first. */
    _inView() {
        const hasMap = this._hasMap();
        return visibleFeatures(
            this._filtered(),
            hasMap ? this._map.getBounds() : null,
            hasMap ? this._map.getCenter() : null,
        );
    }

    pick(event) {
        const slug = event.currentTarget.dataset.slug;
        const feature = this._features.find((f) => f.properties.slug === slug);
        if (feature) this._select(feature, true);
    }

    _select(feature, fly) {
        this._selected = feature.properties.slug;
        this._markSelectedCard();
        if (!this._map) return;
        const point = this._features.find((f) => f.properties.slug === this._selected) || feature;
        this._map.getSource(SELECTED).setData({ type: 'FeatureCollection', features: [point] });
        this._showPopup(point);
        if (fly) {
            this._setSheet('peek');
            this._map.flyTo({ center: point.geometry.coordinates, zoom: Math.max(this._map.getZoom(), PICK_ZOOM) });
        }
    }

    _showPopup(feature) {
        if (this._popup) this._popup.remove();
        this._popup = new window.maplibregl.Popup({ offset: 14, closeButton: true, maxWidth: '260px' })
            .setLngLat(feature.geometry.coordinates)
            .setDOMContent(buildPopup(feature))
            .addTo(this._map);
    }

    _markSelectedCard() {
        for (const card of this.listTarget.children) {
            const on = card.dataset.slug === this._selected;
            card.classList.toggle('find-card--selected', on);
            if (on) card.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }

    // ------------------------------------------------------------------
    // Filters
    // ------------------------------------------------------------------

    filter(event) {
        this._setTrade(event.currentTarget.dataset.trade);
        this._apply();
    }

    /** Press one chip, the same way for a tap and for an interpreted search. */
    _setTrade(slug) {
        this.tradeValue = slug;
        for (const chip of this.chipTargets) {
            const on = chip.dataset.trade === slug;
            chip.setAttribute('aria-pressed', on ? 'true' : 'false');
            if (on) chip.scrollIntoView({ block: 'nearest', inline: 'center' });
        }
        const url = new URL(window.location.href);
        if (slug) url.searchParams.set('trade', slug);
        else url.searchParams.delete('trade');
        window.history.replaceState(window.history.state, '', url);
    }

    search() {
        clearTimeout(this._searchTimer);
        this._searchTimer = setTimeout(() => {
            this._query = this.searchTarget.value.trim();
            this._apply();
            if (this._query && !this._filtered().length) {
                this.countTarget.textContent = 'No name matches. Press Enter and we will work it out.';
            }
        }, SEARCH_DELAY_MS);
    }

    /**
     * Enter in the search box. Text that already matches a name, suburb or
     * trade stays a plain filter; anything else ("handyman near mayfield")
     * goes to the server, which answers with the filters a visitor would
     * otherwise set by hand.
     */
    async ask(event) {
        event.preventDefault();
        clearTimeout(this._searchTimer);
        const text = this.searchTarget.value.trim();
        this._query = text;
        if (!text || this._filtered().length) {
            this._apply();
            if (text) this._reportIfEmpty({ query: text }, false);
            return;
        }
        this.searchTarget.setAttribute('aria-busy', 'true');
        this.countTarget.textContent = 'Working out your search…';
        try {
            this._applySearch(text, await this._postSearch(text));
        } catch (_error) {
            this._apply();
            this.countTarget.textContent = 'Could not work that out. Pick a trade below.';
            this._reportIfEmpty({ query: text }, false);
        } finally {
            this.searchTarget.removeAttribute('aria-busy');
        }
    }

    async _postSearch(text) {
        const response = await fetch(this.searchUrlValue, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRFToken': this.csrfValue },
            body: JSON.stringify({ instruction: text }),
        });
        if (!response.ok) throw new Error(`search ${response.status}`);
        return response.json();
    }

    _applySearch(text, filters) {
        this._setTrade(filters.trade || '');
        this.searchTarget.value = filters.name || '';
        this._query = filters.name || '';
        this.searchTarget.blur();
        const flying = !!(filters.center && this._map);
        if (flying) {
            this._setSheet('half');
            this._map.flyTo({ center: filters.center, zoom: PLACE_ZOOM });
        }
        this._apply();
        this._reportIfEmpty({ query: text, trade: filters.trade || null, place: filters.place || null }, flying);
    }

    /**
     * Once a submitted search has settled (after the map's flight, when it
     * moves), tell the server if it left no business in view. The log of
     * these is the record of where /find has no supply.
     */
    _reportIfEmpty(miss, flying) {
        const check = () => {
            if (!this._features.length || this._inView().length) return;
            const center = this._hasMap() ? this._map.getCenter() : null;
            fetch(this.missedUrlValue, {
                method: 'POST',
                keepalive: true,
                headers: { 'Content-Type': 'application/json', 'X-CSRFToken': this.csrfValue },
                body: JSON.stringify({ ...miss, center: center ? [center.lng, center.lat] : null }),
            }).catch(() => {});
        };
        if (flying) this._map.once('moveend', check);
        else check();
    }

    nearMe() {
        if (!this._map || !navigator.geolocation) return;
        this.nearTarget.classList.add('find-near--busy');
        navigator.geolocation.getCurrentPosition(
            (position) => {
                this.nearTarget.classList.remove('find-near--busy');
                const here = [position.coords.longitude, position.coords.latitude];
                this._placeUser(here);
                this._map.flyTo({ center: here, zoom: NEAR_ZOOM });
            },
            () => {
                this.nearTarget.classList.remove('find-near--busy');
                this.nearTarget.title = 'Location is off for this site';
            },
            { enableHighAccuracy: false, timeout: 8000 },
        );
    }

    _placeUser(lngLat) {
        if (this._userMarker) this._userMarker.remove();
        const dot = document.createElement('span');
        dot.className = 'find-user-dot';
        this._userMarker = new window.maplibregl.Marker({ element: dot }).setLngLat(lngLat).addTo(this._map);
    }

    // ------------------------------------------------------------------
    // Bottom sheet (phone)
    // ------------------------------------------------------------------

    toggleSheet() {
        if (this._dragMoved) return;
        this._setSheet(this._sheet === 'peek' ? 'half' : (this._sheet === 'half' ? 'full' : 'peek'));
    }

    sheetTargetConnected(sheet) {
        const handle = sheet.querySelector('.find-handle');
        this._onDragStart = (event) => this._startDrag(event);
        handle.addEventListener('pointerdown', this._onDragStart);
    }

    sheetTargetDisconnected(sheet) {
        const handle = sheet.querySelector('.find-handle');
        if (handle && this._onDragStart) handle.removeEventListener('pointerdown', this._onDragStart);
    }

    _setSheet(state) {
        this._sheet = state;
        this.sheetTarget.dataset.sheet = state;
        this.sheetTarget.style.transform = '';
        // The map's visible area changed: let it re-measure after the slide.
        if (this._map) setTimeout(() => this._map.resize(), 260);
    }

    _startDrag(event) {
        this._dragStartY = event.clientY;
        this._dragMoved = false;
        this._onDragMove = (move) => {
            const dy = move.clientY - this._dragStartY;
            if (Math.abs(dy) > 6) this._dragMoved = true;
            if (this._dragMoved) this.sheetTarget.style.transform = `translateY(${Math.max(dy, -window.innerHeight)}px)`;
        };
        this._onDragEnd = (end) => {
            const dy = end.clientY - this._dragStartY;
            this._endDrag();
            if (this._dragMoved) this._setSheet(nextSheetState(this._sheet, dy));
            setTimeout(() => { this._dragMoved = false; }, 0);
        };
        window.addEventListener('pointermove', this._onDragMove);
        window.addEventListener('pointerup', this._onDragEnd);
    }

    _endDrag() {
        if (this._onDragMove) window.removeEventListener('pointermove', this._onDragMove);
        if (this._onDragEnd) window.removeEventListener('pointerup', this._onDragEnd);
        this._onDragMove = null;
        this._onDragEnd = null;
    }
}

// ----------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------

/** A drag up of more than 60px opens the sheet a step; down closes a step. */
function nextSheetState(current, dy) {
    const index = SHEET_STATES.indexOf(current);
    if (dy < -60) return SHEET_STATES[Math.min(index + 1, SHEET_STATES.length - 1)];
    if (dy > 60) return SHEET_STATES[Math.max(index - 1, 0)];
    return current;
}

function emptyCollection() {
    return { type: 'FeatureCollection', features: [] };
}

let webglSupported = null;
function hasWebGL() {
    if (webglSupported === null) {
        const canvas = document.createElement('canvas');
        webglSupported = !!(canvas.getContext('webgl2') || canvas.getContext('webgl'));
    }
    return webglSupported;
}

function clusterLayer(colours) {
    return {
        id: 'find-clusters',
        type: 'circle',
        source: SOURCE,
        filter: ['has', 'point_count'],
        paint: {
            'circle-color': colours.cluster,
            'circle-opacity': 0.92,
            'circle-radius': ['step', ['get', 'point_count'], 17, 10, 22, 50, 28, 200, 34],
            'circle-stroke-color': '#FFFFFF',
            'circle-stroke-width': 3,
        },
    };
}

function clusterCountLayer() {
    return {
        id: 'find-cluster-count',
        type: 'symbol',
        source: SOURCE,
        filter: ['has', 'point_count'],
        layout: {
            'text-field': ['get', 'point_count_abbreviated'],
            'text-font': CLUSTER_FONT,
            'text-size': 14,
            'text-allow-overlap': true,
        },
        paint: { 'text-color': '#FFFFFF' },
    };
}

function pointLayer(colours) {
    return {
        id: 'find-points',
        type: 'circle',
        source: SOURCE,
        filter: ['!', ['has', 'point_count']],
        paint: {
            'circle-color': ['match', ['get', 'kind'], 'chippie', colours.chippie, colours.listing],
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 5, 14, 9, 17, 12],
            'circle-stroke-color': '#FFFFFF',
            'circle-stroke-width': 2.5,
        },
    };
}

function selectedLayer(colours) {
    return {
        id: 'find-selected-ring',
        type: 'circle',
        source: SELECTED,
        paint: {
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 11, 14, 16, 17, 20],
            'circle-color': 'rgba(0,0,0,0)',
            'circle-stroke-color': colours.chippie,
            'circle-stroke-width': 3,
        },
    };
}
